import { AnchoredPopup, IconButton, useAnchoredPopupAnchor } from "@carbon6/c6-frontend-design";
import { useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import type { MemoPreview } from "@/api";
import { MemoDetailModal } from "./MemoDetailModal";
import { MemoIcon } from "./MemoIcon";
import { MemoPanel } from "./MemoPanel";
import { useMemoController } from "./useMemoController";
import styles from "./MemoPopover.module.css";

// 메모 인디케이터다. 같은 앵커 팝업(빠른 보기/작성)을 두 형태로 연다:
//   - 표에서는 아이콘+개수 배지.
//   - 상세의 좌측 외부 슬롯에서는 `메모 N` 텍스트.
// 팝업 헤더의 "전체 보기"는 더 넓은 모달을 연다. 행 안에서 써도 안전하도록 트리거는
// stopPropagation 한다. 작성/삭제 후 목록 갱신은 controller 가 ListRefresh 컨텍스트로 처리한다.
export function MemoPopover({
  datasetUuid,
  count = 0,
  latest,
  trigger = "icon",
  size = "sm",
}: {
  datasetUuid: string;
  count?: number;
  latest?: MemoPreview | null;
  trigger?: "icon" | "label";
  size?: "sm" | "md";
}): ReactNode {
  const popup = useAnchoredPopupAnchor();
  const [modalOpen, setModalOpen] = useState(false);
  // 상세의 label 트리거는 진입 즉시 실제 개수를 표시해야 하므로 미리 조회한다. 목록 행의 icon
  // 트리거는 행마다 요청이 생기지 않도록 팝업을 연 뒤에만 로드한다.
  const controller = useMemoController(datasetUuid, {
    enabled: trigger === "label" || popup.open,
  });

  // 상세 label 트리거는 미리 조회한 결과와 작성·삭제 결과를 즉시 반영한다. 닫힌 목록 행은 기존
  // summary count 를 그대로 쓴다. 그래서 행마다 메모 목록을 미리 로드하지 않는다.
  const resolvedCount =
    trigger === "label" && controller.state.status === "success"
      ? controller.state.data.length
      : count;
  const hasMemo = resolvedCount > 0 || latest != null;
  const countLabel = "메모 " + resolvedCount;
  const label = trigger === "label" ? countLabel : hasMemo ? countLabel + "개" : "메모";
  const tooltip = hasMemo ? countLabel + "개를 확인합니다." : "메모를 작성합니다.";

  function handleTriggerClick(event: ReactMouseEvent<HTMLButtonElement>): void {
    event.stopPropagation();
    popup.openFromElement(event);
  }

  function handleExpand(): void {
    popup.close();
    setModalOpen(true);
  }

  return (
    <span className={styles.anchor}>
      <IconButton
        aria-label={label}
        title={tooltip}
        size={size}
        variant="surface"
        selected={popup.open}
        aria-expanded={popup.open}
        aria-haspopup="dialog"
        text={
          trigger === "label" ? countLabel : resolvedCount > 0 ? String(resolvedCount) : undefined
        }
        onClick={handleTriggerClick}
      >
        {trigger === "icon" ? <MemoIcon /> : null}
      </IconButton>
      <AnchoredPopup
        open={popup.open}
        anchor={popup.anchor}
        onClose={popup.close}
        placement="bottom-end"
        role="dialog"
        aria-label="메모"
        width="22rem"
        maxHeight="28rem"
      >
        <AnchoredPopup.Header divider className={styles.header}>
          <span className={styles.title}>메모</span>
          <IconButton
            aria-label="메모 전체 보기"
            title="전체 메모를 확인합니다."
            size="sm"
            variant="surface"
            onClick={handleExpand}
          >
            <ExpandIcon />
          </IconButton>
        </AnchoredPopup.Header>
        <AnchoredPopup.Body padding="md" scroll>
          <MemoPanel controller={controller} compact />
        </AnchoredPopup.Body>
      </AnchoredPopup>
      <MemoDetailModal
        open={modalOpen}
        datasetUuid={datasetUuid}
        onClose={() => setModalOpen(false)}
      />
    </span>
  );
}

function ExpandIcon(): ReactNode {
  return (
    <svg
      aria-hidden="true"
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9.5 2.5h4v4M13.5 2.5 8.5 7.5M6.5 13.5h-4v-4M2.5 13.5l5-5" />
    </svg>
  );
}
